import { Injectable } from '@angular/core';
import { CallHistoryI, CreateCallHistoryI } from '@interfaces/call-history.interface';
import { CallHistoryService, PendingTabulationI } from '@modules/call-history/call-history.service';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { DialerStatusI, SipDataI } from './interfaces/chat-dialer.interface';
import { CallHistoryStatusE, CallHistoryTypeE } from '@enums/call-history.enum';
import { ContactI } from '@interfaces/contact.interface';
import { TeamsI } from '@interfaces/teams.interface';
import { pendingCallsStorageKey, unfinishedCallStorageKey } from '@constants/unfinised-call-storage-key';
import { CreateTabulationI } from '@interfaces/tabulation.interface';

interface PendingCallOperationI {
  op: 'create' | 'update';
  id?: string;
  data: CreateCallHistoryI;
  attempts: number;
}

@Injectable({
  providedIn: 'root',
})
export class CallHistoryManagerService {
  private currentCallSubject = new BehaviorSubject<CallHistoryI | undefined>(undefined);

  public currentCall: CallHistoryI | undefined = undefined;
  public currentCall$ = this.currentCallSubject.asObservable();

  constructor(private callHistoryService: CallHistoryService) {}

  private setStore(call: CallHistoryI | undefined) {
    this.setCurrentCall(call);
    this.saveTempLocalStorage();
  }

  public setCurrentCall(call: CallHistoryI | undefined) {
    this.currentCall = call;
    this.currentCallSubject.next(this.currentCall);
  }

  public async createInitiated(callData: CreateCallHistoryI): Promise<CallHistoryI | undefined> {
    callData.status = CallHistoryStatusE.CALL_INITIATED;
    return await this.create(callData);
  }

  public async create(callData: CreateCallHistoryI): Promise<CallHistoryI | undefined> {
    try {
      const callHistory = await this.callHistoryService.insertCall(callData);
      this.setStore(callHistory);
    } catch (error) {
      console.error('Error creating call history:', error);
      this.enqueuePending('create', callData);
    }
    return this.currentCall;
  }

  public async createOrUpdate(callData: CreateCallHistoryI): Promise<CallHistoryI | undefined> {
    if (this.currentCall?._id) {
      return await this.update(callData);
    }

    return await this.create(callData);
  }

  public async update(callData: CreateCallHistoryI): Promise<CallHistoryI | undefined> {
    if (!this.currentCall?._id) {
      return;
    }
    const id = this.currentCall._id;
    try {
      const updatedCall = await this.callHistoryService.updateCall(id, callData);
      this.setStore(updatedCall);
      return updatedCall;
    } catch (error) {
      console.error('Error updating call history:', error);
      this.enqueuePending('update', callData, id);
    }
    return;
  }

  public async createInitiatedByDataSip(
    sipData: SipDataI,
    dialerStatus?: DialerStatusI
  ): Promise<CallHistoryI | undefined> {
    const callData = this.buildCallData(sipData, dialerStatus);
    callData.status = CallHistoryStatusE.CALL_INITIATED;
    return await this.create(callData);
  }

  public async createOrUpdateByDataSip(
    sipData: SipDataI,
    dialerStatus?: DialerStatusI,
    infoTabulation?: CreateTabulationI
  ): Promise<CallHistoryI | undefined> {
    const callData = this.buildCallData(sipData, dialerStatus, infoTabulation);
    return await this.createOrUpdate(callData);
  }

  public buildCallData(
    sipData: SipDataI,
    dialerStatus?: DialerStatusI,
    infoTabulation?: CreateTabulationI
  ): CreateCallHistoryI {
    const protocol = dialerStatus?.protocol;
    const startDateTxt = sipData.hourIn || sipData.hourOut;
    const startDate = startDateTxt ? new Date(startDateTxt) : undefined;
    const endDate = sipData.hourOut ? new Date(sipData.hourOut) : undefined;
    const contact = dialerStatus?.contact;
    const team = dialerStatus?.team;
    const tabulationIds = infoTabulation?.tabulationIds;
    const commentTabulation = infoTabulation?.commentTabulation;

    return {
      protocol,
      externalCallId: sipData?.externalCallId,
      uniqueId: String(sipData?.timestamp),
      type: sipData?.type === 'local' ? CallHistoryTypeE.LOCAL : CallHistoryTypeE.REMOTE,
      source: sipData?.src || '',
      destination: sipData?.dst || '',
      date: sipData?.date,
      duration: sipData?.duration || '0',
      status: Object.values(CallHistoryStatusE).includes(sipData?.status as CallHistoryStatusE)
        ? (sipData?.status as CallHistoryStatusE)
        : CallHistoryStatusE.UNDEFINED,
      startDate,
      endDate,
      queueId: sipData?.queueId,
      team: team ? this.getPayloadTeamToCall(team) : null,
      contact: contact ? this.getPayloadContactToCall(contact) : undefined,
      tabulationIds,
      commentTabulation,
      anonymous: sipData?.anonymous ?? false,
    };
  }

  private getPayloadTeamToCall(team: TeamsI | undefined) {
    return {
      _id: team?._id,
      customer: team?.customer,
      name: team?.name,
    };
  }

  private getPayloadContactToCall(contact: ContactI | undefined) {
    return {
      _id: contact?._id,
      customerId: contact?.customerId,
      name: contact?.name,
      telephone: contact?.telephone,
      telephones: contact?.telephones,
      email: contact?.email,
      emails: contact?.emails,
      source: contact?.source,
    };
  }

  public async updateTabulation(infoTabulation: { commentTabulation: string; tabulationIds: string[] }): Promise<void> {
    const callHistoryId = this.currentCall?._id;

    if (!callHistoryId || !infoTabulation?.tabulationIds.length) {
      return;
    }

    try {
      await firstValueFrom(
        this.callHistoryService.createTabulation({
          callHistoryId: callHistoryId,
          tabulationIds: infoTabulation?.tabulationIds,
          ...(infoTabulation?.commentTabulation ? { comment: infoTabulation?.commentTabulation } : {}),
        })
      );
    } catch (error) {
      console.error('Error updating tabulation in call history:', error);
    }
  }

  public async setPendingTabulation(payload: PendingTabulationI): Promise<void> {
    if (!payload?.callHistoryId) {
      return;
    }
    try {
      await firstValueFrom(this.callHistoryService.setPendingTabulation(payload));
    } catch (error) {
      console.error('Error setting pending tabulation:', error);
    }
  }

  public async getPendingTabulation(): Promise<PendingTabulationI | null> {
    try {
      return await firstValueFrom(this.callHistoryService.getPendingTabulation());
    } catch (error) {
      console.error('Error fetching pending tabulation:', error);
      return null;
    }
  }

  public async concludeTabulationById(
    callHistoryId: string,
    infoTabulation: { tabulationIds: string[]; commentTabulation?: string }
  ): Promise<void> {
    if (!callHistoryId || !infoTabulation?.tabulationIds?.length) {
      return;
    }
    try {
      await firstValueFrom(
        this.callHistoryService.createTabulation({
          callHistoryId,
          tabulationIds: infoTabulation.tabulationIds,
          ...(infoTabulation.commentTabulation ? { comment: infoTabulation.commentTabulation } : {}),
        })
      );
    } catch (error) {
      console.error('Error concluding tabulation in call history:', error);
    }
  }

  public clear() {
    this.setStore(undefined);
    this.removeTempLocalStorage();
  }

  private saveTempLocalStorage() {
    const callHistoryId = this.currentCall?._id;
    if (!callHistoryId) {
      return;
    }
    localStorage.setItem(unfinishedCallStorageKey, JSON.stringify(this.currentCall));
  }

  public updateTempLocalStorage(call: Partial<CreateCallHistoryI>) {
    const callSave = { ...(this.currentCall || {}), ...call };
    localStorage.setItem(unfinishedCallStorageKey, JSON.stringify(callSave));
  }

  private removeTempLocalStorage() {
    localStorage.removeItem(unfinishedCallStorageKey);
  }

  public getTempLocalStorage(): CreateCallHistoryI | undefined {
    const callData = localStorage.getItem(unfinishedCallStorageKey);
    return callData ? JSON.parse(callData) : undefined;
  }

  private readonly maxPendingAttempts = 10;
  private flushing = false;

  private getPending(): PendingCallOperationI[] {
    try {
      const raw = localStorage.getItem(pendingCallsStorageKey);
      return raw ? (JSON.parse(raw) as PendingCallOperationI[]) : [];
    } catch (error) {
      console.error('Error reading pending call history queue:', error);
      return [];
    }
  }

  private savePending(queue: PendingCallOperationI[]) {
    if (!queue.length) {
      localStorage.removeItem(pendingCallsStorageKey);
      return;
    }
    localStorage.setItem(pendingCallsStorageKey, JSON.stringify(queue));
  }

  private enqueuePending(op: 'create' | 'update', data: CreateCallHistoryI, id?: string) {
    try {
      const queue = this.getPending();
      queue.push({ op, id, data, attempts: 0 });
      this.savePending(queue);
    } catch (error) {
      console.error('Error enqueuing pending call history:', error);
    }
  }

  public async flushPending(): Promise<void> {
    if (this.flushing) {
      return;
    }
    const queue = this.getPending();
    if (!queue.length) {
      return;
    }
    this.flushing = true;
    try {
      const remaining: PendingCallOperationI[] = [];
      for (const item of queue) {
        try {
          if (item.op === 'update' && item.id) {
            await this.callHistoryService.updateCall(item.id, item.data);
          } else {
            await this.callHistoryService.insertCall(item.data);
          }
        } catch (error) {
          console.error('Error flushing pending call history:', error);
          const attempts = (item.attempts ?? 0) + 1;
          if (attempts < this.maxPendingAttempts) {
            remaining.push({ ...item, attempts });
          }
        }
      }
      this.savePending(remaining);
    } finally {
      this.flushing = false;
    }
  }
}
